import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';
import type { Environment } from '@noths/polaris-server-content-fetching';
import { stackConfig } from '@noths/polaris-server-content-fetching';

import {
  INSPIRATION_CONTENTSTACK_CACHE_TTL_SECONDS,
  inspirationAllowCaching,
} from 'src/constants/inspirationCache';
import { withRedisFetchCache } from 'src/services/withRedisFetchCache';
import { getContentstackBranch } from './getContentstackBranch';
import { getContentstackEnvironment } from './utils';

const stackName = 'DX' as const;

const INSPIRATION_TERMS_REQUEST_TIMEOUT_MS = 3000;

/** The taxonomy whose terms become the hub's category tabs. */
export const INSPIRATION_CATEGORY_TAXONOMY_UID = 'blog_taxonomy_category';

/** Term display names keyed by term uid. */
export type InspirationTermNames = Record<string, string>;

const cacheKey = (env: Environment, branch: string, taxonomyUid: string) =>
  `inspiration-term-names-v1-${env}-${stackName}-${branch}-${taxonomyUid}`;

interface ServiceTaxonomyTerm {
  name?: string | null;
  uid?: string | null;
}

export const mapTaxonomyTerms = (terms: ServiceTaxonomyTerm[]): InspirationTermNames =>
  Object.fromEntries(
    terms.flatMap(({ name, uid }) =>
      typeof uid === 'string' && uid && typeof name === 'string' && name.trim()
        ? [[uid, name.trim()]]
        : [],
    ),
  );

// GraphQL returns `term_name` as null on entries and nothing from
// `all_sys_taxonomy_terms`, so the names come from the REST taxonomy endpoint.
// Move this to GraphQL once Contentstack returns the names there.
// `server-content-fetching` only builds `/v3/content_types/...` URLs, hence the
// request is made here.
const fetchTermNamesUncached = async (
  env: Environment,
  branch: string,
  taxonomyUid: string,
): Promise<InspirationTermNames> => {
  const url = new URL(`https://cdn.contentstack.io/v3/taxonomies/${taxonomyUid}/terms`);
  url.searchParams.set('environment', stackConfig[stackName].envs[env].name);

  const response = await fetchJSONWithTimeout(url.toString(), {
    headers: {
      api_key: stackConfig[stackName].apiKey,
      access_token: stackConfig[stackName].envs[env].token,
      branch,
    },
    timeout: INSPIRATION_TERMS_REQUEST_TIMEOUT_MS,
  });

  if (!response.ok) {
    throw new Error(`Inspiration terms for ${taxonomyUid} failed (${response.status})`);
  }

  const body = (await response.json()) as { terms?: ServiceTaxonomyTerm[] };

  return mapTaxonomyTerms(body.terms ?? []);
};

export const fetchInspirationTermNames = async (
  taxonomyUid: string,
  isPreview?: boolean,
): Promise<InspirationTermNames> => {
  const env = getContentstackEnvironment(isPreview);
  const branch = getContentstackBranch();

  return withRedisFetchCache({
    allowCaching: inspirationAllowCaching(env),
    cacheKey: cacheKey(env, branch, taxonomyUid),
    cacheTTLInSeconds: INSPIRATION_CONTENTSTACK_CACHE_TTL_SECONDS,
    fallbackOnError: {} as InspirationTermNames,
    fetcher: () => fetchTermNamesUncached(env, branch, taxonomyUid),
  });
};
